import type { ElevationSourceQualification } from '../../types/elevationSourceQualification';

export function unknownElevationQualification(version: string): ElevationSourceQualification {
  return { schemaVersion: 1, datasetId: 'usgs-3dep', datasetVersion: version,
    publication: { basis: 'unknown', precision: 'unknown' },
    acquisition: { basis: 'not-established-for-query', date: null },
    horizontal: { crs: [], transformation: 'not-applied' },
    vertical: { unit: 'm', unitBasis: 'published-product-contract', datum: null, datumBasis: 'not-established', transformation: 'not-applied' },
    limitations: ['Source acquisition dates require a qualified query-footprint match. Publication or harvest dates are not acquisition dates.'] };
}

function nativeDate(value: unknown): boolean {
  return typeof value === 'string' && (/^\d{4}$/.test(value) || (/^\d{4}-\d{2}-\d{2}$/.test(value)
    && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0,10) === value));
}
export function isValidElevationQualification(input: unknown, version: string): input is ElevationSourceQualification {
  if (!input || typeof input !== 'object') return false;
  const value = input as ElevationSourceQualification;
  if (value.schemaVersion !== 1 || value.datasetId !== 'usgs-3dep' || value.datasetVersion !== version
    || !['unknown', 'mixed-source-publication'].includes(value.publication?.basis)
    || !['unknown', 'mixed'].includes(value.publication?.precision)
    || (value.publication.earliest !== undefined && !nativeDate(value.publication.earliest))
    || (value.publication.latest !== undefined && !nativeDate(value.publication.latest))
    || value.acquisition?.basis !== 'not-established-for-query' || value.acquisition.date !== null
    || !Array.isArray(value.horizontal?.crs) || value.horizontal.crs.some(v => typeof v !== 'string')
    || value.horizontal.transformation !== 'not-applied' || value.vertical?.transformation !== 'not-applied'
    || value.vertical.unit !== 'm' || value.vertical.unitBasis !== 'published-product-contract'
    || !['native-raster', 'not-established'].includes(value.vertical.datumBasis)
    || (value.vertical.datum !== null && typeof value.vertical.datum !== 'string')
    || (value.vertical.datumBasis === 'not-established' && value.vertical.datum !== null)
    || !Array.isArray(value.limitations) || value.limitations.some(v => typeof v !== 'string')) return false;
  const p = value.provenance;
  if (p === null) return false;
  if (p) {
    if (typeof p.version !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(p.version) || !/^[a-f0-9]{64}$/.test(p.catalogSha256)) return false;
    const validPin = (pin: typeof p.closure) => pin && typeof pin.path === 'string'
      && pin.path.startsWith(`refdata/usgs-3dep/${p.version}/`) && !pin.path.split('/').includes('..')
      && /^[1-9]\d*$/.test(pin.generation) && /^[a-f0-9]{64}$/.test(pin.sha256);
    if (!validPin(p.closure) || !validPin(p.metadataAudit)) return false;
  }
  return true;
}

/** Fail closed for legacy/direct callers while retaining the raster samples. */
export function resolveElevationQualification(value: ElevationSourceQualification | undefined, version: string): ElevationSourceQualification {
  return isValidElevationQualification(value, version) ? structuredClone(value) : unknownElevationQualification(version);
}

export function elevationQualificationText(value: ElevationSourceQualification): string {
  return `Elevation source: ${value.datasetId} ${value.datasetVersion}. Catalog publication: ${value.publication.basis}`
    + `${value.publication.earliest || value.publication.latest ? ` (${value.publication.earliest ?? 'unknown'} to ${value.publication.latest ?? 'unknown'}; original date precision retained)` : ''}.`
    + ' Acquisition date at the sampled location: not established; catalog publication and harvest dates are not site acquisition dates.'
    + ` Native horizontal CRS: ${value.horizontal.crs.join(', ') || 'not established'}; coordinate transformation: not applied.`
    + ` Vertical unit: metres (${value.vertical.unitBasis}); vertical datum: ${value.vertical.datumBasis === 'native-raster' && value.vertical.datum ? value.vertical.datum : 'not established'}; vertical transformation: not applied.`
    + ` ${value.limitations.join(' ')}`;
}
